import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import { Box, Button, GlobalStyles, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useShop } from '../../../shop/ShopProvider';

export interface PrintColumn<T> {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  render: (row: T) => ReactNode;
}

/**
 * Reusable A4 document (UI_UX_GUIDELINES "Printing"): shop branding, a meta block, a lines table
 * whose header repeats on every page, a totals block, and the document number with page x/y in the
 * footer. On screen it is shown as a sheet of paper with a print toolbar; when printing only the
 * sheet is printed (navigation and buttons are hidden).
 */
export function PrintDocument<T>({
  title,
  number,
  status,
  backTo,
  party,
  meta,
  columns,
  rows,
  rowKey,
  totals,
  notes,
  children,
}: {
  title: string;
  number: string;
  status?: string;
  backTo: string;
  party?: { label: string; name: string; lines?: (string | null | undefined)[] };
  meta: { label: string; value: ReactNode }[];
  columns: PrintColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  totals: { label: string; value: ReactNode; strong?: boolean }[];
  notes?: (string | null | undefined)[];
  children?: ReactNode;
}) {
  const { settings } = useShop();
  const navigate = useNavigate();
  const prefs = settings?.printPreferences;
  const footer = [prefs?.footerText, prefs?.termsText].filter(Boolean);

  return (
    <>
      <GlobalStyles
        styles={{
          '@page': {
            size: 'A4',
            margin: '14mm 14mm 18mm',
            '@bottom-left': {
              content: `"${number.replace(/"/g, '')}"`,
              fontSize: '9pt',
              color: '#667085',
            },
            '@bottom-right': {
              content: '"Page " counter(page) " of " counter(pages)',
              fontSize: '9pt',
              color: '#667085',
            },
          },
          '@media print': {
            // Light paper even in dark mode (the dark scheme paints the page margins dark).
            ':root': { colorScheme: 'light !important' },
            'html, body': { background: '#fff !important' },
            'body *': { visibility: 'hidden' },
            '.sm-print-root, .sm-print-root *': { visibility: 'visible' },
            '.sm-print-root': {
              position: 'absolute',
              inset: '0 auto auto 0',
              width: '100%',
              boxShadow: 'none !important',
              border: '0 !important',
              padding: '0 !important',
              margin: '0 !important',
              color: '#101828 !important',
              background: '#fff !important',
            },
            '.sm-print-root thead': { display: 'table-header-group' },
            '.sm-print-root tr': { breakInside: 'avoid' },
            '.sm-no-print': { display: 'none !important' },
          },
        }}
      />
      <Stack
        className="sm-no-print"
        direction="row"
        spacing={1.25}
        sx={{ mb: 2.5, justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Button
          startIcon={<ArrowBackRounded />}
          onClick={() => void navigate(backTo)}
          sx={{ color: 'text.secondary' }}
        >
          Back
        </Button>
        <Button variant="contained" startIcon={<PrintRounded />} onClick={() => window.print()}>
          Print
        </Button>
      </Stack>

      <Box
        className="sm-print-root"
        sx={{
          mx: 'auto',
          width: '100%',
          maxWidth: '210mm',
          minHeight: { md: '297mm' },
          p: { xs: 3, sm: '14mm' },
          bgcolor: '#fff',
          color: '#101828',
          borderRadius: '6px',
          boxShadow: '0 1px 2px rgba(16,24,40,.06), 0 24px 48px -16px rgba(16,24,40,.18)',
          fontSize: '10pt',
          '& .tabular, & td': { fontVariantNumeric: 'tabular-nums' },
        }}
      >
        {/* Letterhead */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{
            justifyContent: 'space-between',
            pb: 2.5,
            mb: 3,
            borderBottom: '2px solid',
            borderColor: 'var(--mui-palette-primary-main)',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
                fontWeight: 750,
                fontSize: '17pt',
                lineHeight: 1.2,
                color: 'var(--mui-palette-primary-main)',
              }}
            >
              {settings?.name ?? 'Shop'}
            </Typography>
            {[
              settings?.address,
              [settings?.phone, settings?.email].filter(Boolean).join(' · '),
              prefs?.showTaxNumber && settings?.taxNumber ? `Tax no. ${settings.taxNumber}` : null,
            ]
              .filter(Boolean)
              .map((l) => (
                <Typography key={l} sx={{ fontSize: '9pt', color: '#475467' }}>
                  {l}
                </Typography>
              ))}
          </Box>
          <Box sx={{ textAlign: { sm: 'right' } }}>
            <Typography
              sx={{
                fontSize: '8.5pt',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#667085',
                fontWeight: 650,
              }}
            >
              {title}
            </Typography>
            <Typography sx={{ fontWeight: 750, fontSize: '15pt' }}>{number}</Typography>
            {status && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  mt: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: '4px',
                  border: '1px solid #D0D5DD',
                  fontSize: '8pt',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                {status}
              </Box>
            )}
          </Box>
        </Stack>

        {/* Party and meta */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }}>
          {party && (
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '8pt', color: '#667085', fontWeight: 650 }}>
                {party.label.toUpperCase()}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '11pt' }}>{party.name}</Typography>
              {party.lines?.filter(Boolean).map((l) => (
                <Typography key={l} sx={{ fontSize: '9pt', color: '#475467' }}>
                  {l}
                </Typography>
              ))}
            </Box>
          )}
          <Box
            component="dl"
            sx={{
              flex: 1,
              m: 0,
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: 2,
              rowGap: 0.5,
              alignContent: 'start',
            }}
          >
            {meta.map((m) => (
              <Box key={m.label} sx={{ display: 'contents' }}>
                <Typography component="dt" sx={{ fontSize: '9pt', color: '#667085' }}>
                  {m.label}
                </Typography>
                <Typography
                  component="dd"
                  sx={{ m: 0, fontSize: '9pt', fontWeight: 600, textAlign: 'right' }}
                >
                  {m.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Stack>

        {/* Lines */}
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '9pt',
              '& th': {
                textAlign: 'left',
                fontWeight: 650,
                fontSize: '8pt',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#475467',
                bgcolor: '#F2F4F7',
                px: 1.25,
                py: 1,
              },
              '& td': { px: 1.25, py: 1, borderBottom: '1px solid #EAECF0', verticalAlign: 'top' },
            }}
          >
            <thead>
              <tr>
                {columns.map((c) => (
                  <Box
                    component="th"
                    key={c.key}
                    style={{ textAlign: c.align ?? 'left', width: c.width }}
                  >
                    {c.header}
                  </Box>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={rowKey(r)}>
                  {columns.map((c) => (
                    <Box component="td" key={c.key} sx={{ textAlign: c.align ?? 'left' }}>
                      {c.render(r)}
                    </Box>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <Box component="td" colSpan={columns.length} sx={{ color: '#98A2B3' }}>
                    —
                  </Box>
                </tr>
              )}
            </tbody>
          </Box>
        </Box>

        {/* Totals */}
        <Stack sx={{ mt: 2, ml: 'auto', width: { xs: '100%', sm: '55%' } }}>
          {totals.map((t) => (
            <Stack
              key={t.label}
              direction="row"
              sx={{
                justifyContent: 'space-between',
                py: 0.75,
                px: 1.25,
                borderBottom: t.strong ? 0 : '1px solid #EAECF0',
                ...(t.strong && {
                  mt: 0.5,
                  borderRadius: '6px',
                  bgcolor: 'color-mix(in srgb, var(--mui-palette-primary-main) 8%, #fff)',
                }),
              }}
            >
              <Typography
                sx={{ fontSize: t.strong ? '10.5pt' : '9pt', fontWeight: t.strong ? 700 : 500 }}
              >
                {t.label}
              </Typography>
              <Typography
                className="tabular"
                sx={{ fontSize: t.strong ? '10.5pt' : '9pt', fontWeight: t.strong ? 750 : 600 }}
              >
                {t.value}
              </Typography>
            </Stack>
          ))}
        </Stack>

        {children}

        {notes?.filter(Boolean).map((n) => (
          <Typography
            key={n}
            sx={{ mt: 3, fontSize: '9pt', color: '#475467', whiteSpace: 'pre-line' }}
          >
            {n}
          </Typography>
        ))}

        {footer.length > 0 && (
          <Box sx={{ mt: 5, pt: 2, borderTop: '1px solid #EAECF0' }}>
            {footer.map((f) => (
              <Typography
                key={f}
                sx={{ fontSize: '8.5pt', color: '#667085', whiteSpace: 'pre-line' }}
              >
                {f}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}
